// Die Zielordnung der Waffenkanaele — **eine** benannte Ordnung, zwei Sprachen.
//
// Wortgleiche Entsprechung von `backend/farmining/progression/targeting.py`.
//
// Wonach eine Drohne ihr Ziel waehlt, stand zweimal: einmal hier, einmal im
// Server. Beide nahmen „den naechsten freien Brocken", und solange das die ganze
// Regel ist, faellt die Doppelung nicht auf — die Reihenfolge stimmte, weil
// beide nach demselben Abstand sortierten.
//
// Sie stimmte aber nur **fast**. Bei gleichem Abstand entschied auf jeder Seite
// die Ladereihenfolge, und die ist eine andere. Sobald eine Vorliebe dazukommt,
// wird aus der Auswahl eine mehrstufige Ordnung, und zwei Fassungen davon laufen
// still auseinander: der Client zeigt eine Drohne ueber Brocken A, der Server
// bucht B. Kein Fehler, keine Meldung, nur ein Bild, das nicht zur Ausbeute
// passt.

/** Das Wenige, das die Ordnung von einem Ziel braucht. */
export interface Target {
  id: string;
  x: number;
  y: number;
  /** Das Erz — die **Neigung** liest es, sonst niemand in dieser Datei. */
  ore: string;
}

// Der Bezugspunkt steht am Modul und nicht in einer Closure: `orderDroneTargets`
// laeuft einmal je Schritt, und ein neuer Vergleicher je Aufruf waere eine
// Allokation im Hot Path (Gate FG-14).
let orderX = 0;
let orderY = 0;
let orderWish = '';

/**
 * Der Vergleich des Drohnenkanals.
 *
 * 1. **Die Neigung** zuerst: ein Brocken des Wunscherzes kommt vor allen
 *    anderen. Eine Vorliebe, keine Zielauswahl (`GAME-001`) — der Spieler wählt,
 *    *was* seine Drohnen bevorzugt holen, nicht *welchen* Brocken.
 * 2. **Nähe** zum Bezugspunkt — quadriert, weil die Wurzel an der Reihenfolge
 *    nichts ändert und beide Seiten so bitgleich vergleichen.
 * 3. **Die kleinere ID** bei Gleichstand. Ohne diesen Schritt hängt die Auswahl
 *    an der Ladereihenfolge, und die ist auf beiden Seiten eine andere. IDs sind
 *    ASCII, also vergleichen JavaScript und Python sie zeichenweise gleich.
 */
function byDroneOrder(a: Target, b: Target): number {
  const na = orderWish && a.ore === orderWish ? 0 : 1;
  const nb = orderWish && b.ore === orderWish ? 0 : 1;
  if (na !== nb) return na - nb;
  const da = (a.x - orderX) ** 2 + (a.y - orderY) ** 2;
  const db = (b.x - orderX) ** 2 + (b.y - orderY) ** 2;
  if (da !== db) return da - db;
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}

/**
 * Das beste freie Ziel im Umkreis von `(x, y)` — nach der Drohnenordnung.
 *
 * Ein linearer Durchlauf statt einer Sortierung: seit die Drohnen ausschwärmen,
 * hat **jede** ihren eigenen Suchkreis, und einmal je Drohne zu sortieren wäre
 * der Hot Path, den `orderDroneTargets` gerade vermeidet. Verglichen wird
 * derselbe Schlüssel — die Ordnung bleibt eine.
 */
export function pickDroneTarget<T extends Target>(
  candidates: readonly T[],
  taken: ReadonlySet<string>,
  x: number,
  y: number,
  radius: number,
  wish = '',
): T | undefined {
  let best: T | undefined;
  let bestWish = 1;
  let bestDist = Infinity;
  let bestId = '';
  const limit = radius * radius;
  for (const target of candidates) {
    if (taken.has(target.id)) continue;
    const dist = (target.x - x) ** 2 + (target.y - y) ** 2;
    if (dist > limit) continue;
    const neigung = wish && target.ore === wish ? 0 : 1;
    const besser = best === undefined
      || neigung < bestWish
      || (neigung === bestWish && (dist < bestDist || (dist === bestDist && target.id < bestId)));
    if (besser) { best = target; bestWish = neigung; bestDist = dist; bestId = target.id; }
  }
  return best;
}

/**
 * Zielkandidaten **an Ort und Stelle** in die Drohnenordnung bringen.
 *
 * Die **Reservierung** bleibt ein eigener Schritt danach: sie überspringt,
 * worauf ein anderer Kanal schon zielt, statt nachträglich zu filtern. Die
 * Invariante „ein Brocken nimmt pro Schritt aus höchstens einem Kanal Schaden"
 * hängt daran.
 */
export function orderDroneTargets<T extends Target>(
  list: T[], x: number, y: number, wish = '',
): T[] {
  orderX = x;
  orderY = y;
  orderWish = wish;
  list.sort(byDroneOrder);
  return list;
}
